import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000/api";
const TRACKER_FLAG_EVENT = "wfh-tracker-flag-change";
const DEFAULT_LOCK_IDLE_GRACE_SECONDS = 5 * 60;
const DEFAULT_DETECTOR_TRIGGER_SECONDS = 60;
const IS_DEV = process.env.NODE_ENV !== "production";
const FALLBACK_ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
];

const detectorTriggerSeconds = Number.parseInt(
  process.env.REACT_APP_IDLE_DETECTOR_TRIGGER_SECONDS,
  10
);
const lockIdleGraceSeconds = Number.parseInt(
  process.env.REACT_APP_IDLE_LOCK_GRACE_SECONDS,
  10
);

const DETECTOR_THRESHOLD_MS = (
  Number.isFinite(detectorTriggerSeconds) && detectorTriggerSeconds > 0
    ? detectorTriggerSeconds
    : DEFAULT_DETECTOR_TRIGGER_SECONDS
) * 1000;
const LOCK_IDLE_THRESHOLD_MS = (
  Number.isFinite(lockIdleGraceSeconds) && lockIdleGraceSeconds > 0
    ? lockIdleGraceSeconds
    : DEFAULT_LOCK_IDLE_GRACE_SECONDS
) * 1000;

const WATCHDOG_INTERVAL_MS = 15000;
const WAKE_DRIFT_MS = Math.max(45000, DETECTOR_THRESHOLD_MS + 10000);

function notifyTrackerFlagChange() {
  window.dispatchEvent(new Event(TRACKER_FLAG_EVENT));
}

function normalizeScreenState(value) {
  if (value === "locked" || value === "unlocked") {
    return value;
  }
  if (value === "inactive") {
    return "inactive";
  }
  return "active";
}

function getForegroundScreenState() {
  return document.hidden ? "inactive" : "unlocked";
}

function hasOpenTrackingSession() {
  const status = localStorage.getItem("WFHCurrentStatus");
  const wfhId = localStorage.getItem("WFHCurrentWfhId");
  return Boolean(wfhId) && !["idle", "ended", "closed", "", null].includes(status);
}

function logTracker(level, message, payload) {
  if (!IS_DEV) {
    return;
  }

  if (payload === undefined) {
    console[level](message);
    return;
  }

  console[level](message, payload);
}

export async function getIdleTrackingPermissionState() {
  if (!("IdleDetector" in window)) {
    return "unsupported";
  }

  try {
    const status = await navigator.permissions.query({ name: "idle-detection" });
    return status.state;
  } catch (err) {
    logTracker("warn", "[IdleTracker] Permission state query failed:", err.message);
    return "prompt";
  }
}

export async function requestIdleTrackingPermission() {
  if (!("IdleDetector" in window)) {
    return "unsupported";
  }

  try {
    return await window.IdleDetector.requestPermission();
  } catch (err) {
    logTracker("warn", "[IdleTracker] Permission request failed:", err.message);
    return null;
  }
}

export async function ensureIdleTrackingPermission({ interactive = false } = {}) {
  const currentState = await getIdleTrackingPermissionState();
  if (currentState === "granted" || currentState === "unsupported" || !interactive) {
    return currentState;
  }

  return requestIdleTrackingPermission();
}

async function post(endpoint, body, { keepalive = false } = {}) {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { "x-access-token": token } : {}),
    },
    body: JSON.stringify(body),
    keepalive,
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;

    try {
      const data = await response.json();
      message = data?.message || data?.error || message;
    } catch (err) {
      // Ignore response parsing errors for silent background sync.
    }

    throw new Error(message);
  }

  if (response.status === 204) {
    return null;
  }

  try {
    return await response.json();
  } catch (err) {
    return null;
  }
}

export function useIdleTracker({ userId, emp_id } = {}) {
  const location = useLocation();
  const resolvedUserId = userId || emp_id;

  const sessionIdRef = useRef(null);
  const detectorRef = useRef(null);
  const detectorAbortControllerRef = useRef(null);
  const detectorChangeHandlerRef = useRef(null);
  const initialPathRef = useRef(location.pathname);
  const prevPathRef = useRef(null);
  const prevEnteredAtRef = useRef(null);
  const currentStateRef = useRef("active");
  const currentScreenStateRef = useRef("unlocked");
  const lockedAtRef = useRef(null);
  const lastInteractionAtRef = useRef(Date.now());
  const lastPresenceEvidenceAtRef = useRef(Date.now());
  const lastWatchdogTickAtRef = useRef(Date.now());
  const syncQueueRef = useRef(Promise.resolve());
  const pendingSyncRef = useRef(null);

  const [permissionState, setPermissionState] = useState("prompt");
  const [userState, setUserState] = useState("active");
  const [screenState, setScreenState] = useState("unlocked");
  const [isSupported, setIsSupported] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  const getTrackerContext = useCallback(() => {
    const status = localStorage.getItem("WFHCurrentStatus");
    const wfhId = localStorage.getItem("WFHCurrentWfhId");
    return {
      status,
      wfhId,
      isWorking: status === "working" && Boolean(wfhId),
    };
  }, []);

  const syncIdleState = useCallback(
    (nextState, { screenState: nextScreenState, source = "unknown", allowWhenNotWorking = false } = {}) => {
      syncQueueRef.current = syncQueueRef.current
        .catch(() => undefined)
        .then(async () => {
          const normalizedScreenState = normalizeScreenState(
            nextScreenState || currentScreenStateRef.current
          );
          const { isWorking, wfhId } = getTrackerContext();
          const isIdleTrackerEnabled = localStorage.getItem("WFHStartIdleTracker") === "true";

          if (nextState === currentStateRef.current) {
            currentScreenStateRef.current = normalizedScreenState;
            setScreenState(normalizedScreenState);
            return true;
          }

          if (!isIdleTrackerEnabled) {
            logTracker("log", "[IdleTracker] Idle tracking is disabled", {
              source,
              status: localStorage.getItem("WFHCurrentStatus"),
              wfhId,
            });
            return false;
          }

          if (nextState === "idle" && !isWorking) {
            logTracker("log", "[IdleTracker] Idle start ignored without active WFH session", {
              source,
              status: localStorage.getItem("WFHCurrentStatus"),
              wfhId,
            });
            return false;
          }

          if (
            nextState === "active" &&
            !isWorking &&
            !allowWhenNotWorking &&
            currentStateRef.current !== "idle"
          ) {
            return false;
          }

          try {
            if (nextState === "idle") {
              await post("idletracker/start", {
                screenState: normalizedScreenState,
                prevState: currentStateRef.current,
                wfhId,
              }, {
                keepalive: true,
              });
            } else {
              await post("idletracker/end", { wfhId }, { keepalive: true });
            }

            pendingSyncRef.current = null;
            currentStateRef.current = nextState;
            currentScreenStateRef.current = normalizedScreenState;
            setUserState(nextState);
            setScreenState(normalizedScreenState);
            logTracker("log", "[IdleTracker] Synced state", {
              nextState,
              screenState: normalizedScreenState,
              source,
              wfhId,
            });
            return true;
          } catch (err) {
            pendingSyncRef.current = {
              nextState,
              screenState: normalizedScreenState,
              source,
            };
            logTracker("warn", "[IdleTracker] State sync failed", {
              nextState,
              screenState: normalizedScreenState,
              source,
              message: err.message,
            });
            return false;
          }
        });

      return syncQueueRef.current;
    },
    [getTrackerContext]
  );

  const flushPendingSync = useCallback(() => {
    const pendingSync = pendingSyncRef.current;
    if (!pendingSync) {
      return Promise.resolve(true);
    }

    return syncIdleState(pendingSync.nextState, {
      screenState: pendingSync.screenState,
      source: `${pendingSync.source}-retry`,
    });
  }, [syncIdleState]);

  const stopDetector = useCallback(() => {
    if (detectorRef.current && detectorChangeHandlerRef.current) {
      detectorRef.current.removeEventListener("change", detectorChangeHandlerRef.current);
    }
    detectorChangeHandlerRef.current = null;
    detectorRef.current = null;
    if (detectorAbortControllerRef.current) {
      detectorAbortControllerRef.current.abort();
      detectorAbortControllerRef.current = null;
    }
  }, []);

  const markPresence = useCallback(
    (source, { forceSyncActive = false } = {}) => {
      const now = Date.now();
      const nextScreenState = getForegroundScreenState();

      lastPresenceEvidenceAtRef.current = now;
      lockedAtRef.current = null;
      currentScreenStateRef.current = nextScreenState;
      setScreenState(nextScreenState);

      if (forceSyncActive && currentStateRef.current === "idle" && nextScreenState !== "locked") {
        setUserState("active");
        void syncIdleState("active", {
          screenState: nextScreenState,
          source,
        });
      }
    },
    [syncIdleState]
  );

  const handleDetectorChange = useCallback(
    (detector) => {
      const now = Date.now();
      const nextUserState = detector.userState;
      const nextScreenState = normalizeScreenState(detector.screenState);

      currentScreenStateRef.current = nextScreenState;
      setScreenState(nextScreenState);

      if (nextScreenState === "locked") {
        if (!lockedAtRef.current) {
          lockedAtRef.current = now;
        }
        setUserState(currentStateRef.current === "idle" ? "idle" : "active");
        return;
      }

      lockedAtRef.current = null;
      lastPresenceEvidenceAtRef.current = now;

      if (nextUserState === "active") {
        lastInteractionAtRef.current = now;
      }

      if (currentStateRef.current === "idle") {
        setUserState("active");
        void syncIdleState("active", {
          screenState: nextScreenState,
          source: "detector-unlocked",
        });
        return;
      }

      setUserState("active");
    },
    [syncIdleState]
  );

  const startDetector = useCallback(
    async (forceRestart = false) => {
      if (!("IdleDetector" in window)) {
        setIsSupported(false);
        return false;
      }

      if (detectorRef.current && !forceRestart) {
        return true;
      }

      if (forceRestart) {
        stopDetector();
      }

      try {
        const detector = new window.IdleDetector();
        const abortController = new AbortController();
        const handleChange = () => handleDetectorChange(detector);

        detector.addEventListener("change", handleChange);
        await detector.start({
          threshold: DETECTOR_THRESHOLD_MS,
          signal: abortController.signal,
        });

        detectorRef.current = detector;
        detectorAbortControllerRef.current = abortController;
        detectorChangeHandlerRef.current = handleChange;
        setShowBanner(false);
        localStorage.setItem("showBanner", "false");
        notifyTrackerFlagChange();
        logTracker("log", "[IdleTracker] IdleDetector started");

        if (localStorage.getItem("showPermissionGranded") === "true") {
          await Swal.fire({
            icon: "success",
            title: "Permission Granted",
            text: "Permission granted. Click the Login button to start your WFH session.",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          localStorage.setItem("showPermissionGranded", "false");
          localStorage.setItem("WFHStartIdleTracker", "false");
          notifyTrackerFlagChange();
        }

        return true;
      } catch (err) {
        stopDetector();
        logTracker("warn", "[IdleTracker] detector.start() failed:", err.message);
        return false;
      }
    },
    [handleDetectorChange, stopDetector]
  );

  const requestPermission = useCallback(async () => {
    const result = await requestIdleTrackingPermission();
    if (!result || result === "unsupported") {
      return result;
    }

    setPermissionState(result);
    if (result === "granted") {
      await startDetector();
    }

    return result;
  }, [startDetector]);

  useEffect(() => {
    if (!resolvedUserId) return undefined;

    let mounted = true;
    let permissionStatus = null;
    let handlePermissionChange = null;

    (async () => {
      if (!("IdleDetector" in window)) {
        setIsSupported(false);
        logTracker("warn", "[IdleTracker] IdleDetector not supported");
        return;
      }

      try {
        const status = await navigator.permissions.query({ name: "idle-detection" });
        permissionStatus = status;
        setPermissionState(status.state);

        if (status.state === "granted" && mounted) {
          await startDetector();
          setShowBanner(false);
          localStorage.setItem("showBanner", "false");
          notifyTrackerFlagChange();
        } else {
          setShowBanner(true);
          localStorage.setItem("showBanner", "true");
          notifyTrackerFlagChange();
        }

        handlePermissionChange = () => {
          setPermissionState(status.state);

          if (status.state === "granted" && mounted) {
            void startDetector();
            setShowBanner(false);
            localStorage.setItem("showBanner", "false");
            notifyTrackerFlagChange();
            return;
          }

          if (mounted) {
            stopDetector();
            setShowBanner(true);
            localStorage.setItem("showBanner", "true");
            if (hasOpenTrackingSession()) {
              localStorage.setItem("WFHStartIdleTracker", "true");
            }
            notifyTrackerFlagChange();
          }
        };

        status.addEventListener("change", handlePermissionChange);
      } catch (err) {
        logTracker("warn", "[IdleTracker] Permission query failed:", err.message);
        setShowBanner(true);
        localStorage.setItem("showBanner", "true");
        if (hasOpenTrackingSession()) {
          localStorage.setItem("WFHStartIdleTracker", "true");
        }
        notifyTrackerFlagChange();
      }

      prevPathRef.current = initialPathRef.current;
      prevEnteredAtRef.current = new Date().toISOString();
    })();

    return () => {
      mounted = false;
      if (permissionStatus && handlePermissionChange) {
        permissionStatus.removeEventListener("change", handlePermissionChange);
      }
      stopDetector();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedUserId, startDetector, stopDetector]);

  useEffect(() => {
    if (!sessionIdRef.current) return undefined;

    const prev = prevPathRef.current;
    const enteredAt = prevEnteredAtRef.current;

    if (prev && prev !== location.pathname) {
      void post("/page-visit", {
        sessionId: sessionIdRef.current,
        path: location.pathname,
        title: document.title,
        enteredAt: new Date().toISOString(),
        prevPath: prev,
        leftAt: enteredAt,
      }, {
        keepalive: true,
      }).catch(() => undefined);
    }

    prevPathRef.current = location.pathname;
    prevEnteredAtRef.current = new Date().toISOString();
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    if (!resolvedUserId) return undefined;

    const handleUserActivity = () => {
      const now = Date.now();
      const nextScreenState = getForegroundScreenState();

      lastInteractionAtRef.current = now;
      lastPresenceEvidenceAtRef.current = now;
      lockedAtRef.current = null;
      currentScreenStateRef.current = nextScreenState;
      setScreenState(nextScreenState);
      setUserState("active");

      if (currentStateRef.current === "idle") {
        void syncIdleState("active", {
          screenState: nextScreenState,
          source: "user-activity",
        });
      }
    };

    FALLBACK_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true });
    });

    return () => {
      FALLBACK_ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
    };
  }, [resolvedUserId, syncIdleState]);

  useEffect(() => {
    if (!resolvedUserId) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        markPresence("visibility-change", { forceSyncActive: true });
      }
    };

    const handleWindowFocus = () => {
      markPresence("window-focus", { forceSyncActive: true });
    };

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        markPresence("fullscreen-change", { forceSyncActive: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [markPresence, resolvedUserId]);

  useEffect(() => {
    if (!resolvedUserId) return undefined;

    lastWatchdogTickAtRef.current = Date.now();

    const intervalId = window.setInterval(() => {
      const now = Date.now();
      const drift = now - lastWatchdogTickAtRef.current;
      const { isWorking } = getTrackerContext();

      lastWatchdogTickAtRef.current = now;

      if (drift > WAKE_DRIFT_MS && permissionState === "granted") {
        logTracker("log", "[IdleTracker] Sleep/wake drift detected, restarting detector", {
          drift,
        });
        void startDetector(true);
      }

      if (!isWorking) {
        return;
      }

      if (currentScreenStateRef.current === "locked") {
        if (!lockedAtRef.current) {
          lockedAtRef.current = now;
        }

        if (
          currentStateRef.current !== "idle" &&
          now - lockedAtRef.current >= LOCK_IDLE_THRESHOLD_MS
        ) {
          void syncIdleState("idle", {
            screenState: "locked",
            source: "watchdog-locked-threshold",
          });
        }
        return;
      }
    }, WATCHDOG_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [getTrackerContext, permissionState, resolvedUserId, startDetector, syncIdleState]);

  useEffect(() => {
    if (!resolvedUserId) return undefined;

    const recoverDetector = () => {
      lastWatchdogTickAtRef.current = Date.now();
      markPresence("recover-detector", { forceSyncActive: true });
      void flushPendingSync();
      if (permissionState === "granted") {
        void startDetector(true);
      }
    };

    window.addEventListener("focus", recoverDetector);
    window.addEventListener("pageshow", recoverDetector);
    window.addEventListener("online", recoverDetector);

    return () => {
      window.removeEventListener("focus", recoverDetector);
      window.removeEventListener("pageshow", recoverDetector);
      window.removeEventListener("online", recoverDetector);
    };
  }, [flushPendingSync, markPresence, permissionState, resolvedUserId, startDetector]);

  useEffect(() => {
    if (!resolvedUserId) return undefined;

    const handleTrackerContextChange = () => {
      const { isWorking } = getTrackerContext();

      if (!isWorking && currentStateRef.current === "idle") {
        void syncIdleState("active", {
          screenState: getForegroundScreenState(),
          source: "tracker-context-change",
          allowWhenNotWorking: true,
        });
      }

      if (isWorking && permissionState === "granted") {
        void flushPendingSync();
        void startDetector();
      }
    };

    handleTrackerContextChange();
    window.addEventListener(TRACKER_FLAG_EVENT, handleTrackerContextChange);
    window.addEventListener("storage", handleTrackerContextChange);

    return () => {
      window.removeEventListener(TRACKER_FLAG_EVENT, handleTrackerContextChange);
      window.removeEventListener("storage", handleTrackerContextChange);
    };
  }, [flushPendingSync, getTrackerContext, permissionState, resolvedUserId, startDetector, syncIdleState]);

  useEffect(() => {
    if (!resolvedUserId) return undefined;

    const unloadMessage = "WFH tracking is active. Are you sure you want to leave this page?";
    const handleBeforeUnload = (event) => {
      if (!hasOpenTrackingSession()) {
        return undefined;
      }

      event.preventDefault();
      event.returnValue = unloadMessage;
      return unloadMessage;
    };

    window.onbeforeunload = handleBeforeUnload;
    window.addEventListener("beforeunload", handleBeforeUnload, true);
    return () => {
      window.onbeforeunload = null;
      window.removeEventListener("beforeunload", handleBeforeUnload, true);
    };
  }, [resolvedUserId]);

  return {
    userState,
    screenState,
    isIdle: userState === "idle",
    isLocked: screenState === "locked",
    permissionState,
    isSupported,
    showBanner,
    requestPermission,
    startDetector,
  };
}
